import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { webApi } from '@/services/webApi'
import { mergeSiteSettings } from '@/lib/mergeSiteSettings'
import { useCityStore } from '@/store/cityStore'
import type { SiteConfig } from '@/types/settings'
import { SITE } from '@/lib/site'

const fallbackSite = mergeSiteSettings(null)

const SiteSettingsContext = createContext<SiteConfig>(fallbackSite)

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const cityId = useCityStore((s) => s.cityId)

  const { data } = useQuery({
    queryKey: ['web', 'settings', cityId],
    queryFn: () => webApi.settings(cityId ? { cityId } : undefined),
    staleTime: 5 * 60_000,
  })

  const site = useMemo(() => mergeSiteSettings(data), [data])

  return <SiteSettingsContext.Provider value={site}>{children}</SiteSettingsContext.Provider>
}

/** Live admin settings merged over static SITE defaults. */
export function useSite() {
  return useContext(SiteSettingsContext)
}

/** Read a single admin setting by key (e.g. `Property_Listing`). */
export function useSetting(key: string, fallback = ''): string {
  const site = useSite()
  return site.raw[key] ?? fallback
}

export { SITE }
